/* eslint-disable camelcase */
import { Injectable, NgZone } from '@angular/core';
import { environment } from '@environments/environment';
import posthog from 'posthog-js';
import type { User } from './auth.service';

/**
 * PostHog Analytics Service
 * 
 * This service provides a centralized interface for integrating PostHog analytics
 * into the LibraryIQ application. It handles user identification, event tracking,
 * and property management while ensuring privacy and performance.
 * 
 * Key Features:
 * - Automatic user identification on login/session restore
 * - Custom event tracking for user actions
 * - Dynamic property updates (library/branch changes)
 * - Privacy-first approach (no sensitive data tracked)
 * - Graceful error handling
 * - Environment-based enable/disable
 * 
 * @see posthog.service.md for comprehensive documentation
 */

interface PostHogUser {
    id: string | number;
    userName?: string;
    username?: string;
    email?: string;
    accountId?: string | number;
    roleid?: string | number;
    libraryId?: string | number;
    branchId?: string | number;
    language?: string;
    timeZone?: string;
    timezone?: string;
    userId?: string | number;
    libraryName?: string;
    branchName?: string;
}

@Injectable({ providedIn: 'root' })
export class PosthogService {
    private initialized = false;

    constructor(private ngZone: NgZone) {}
    
    /**
     * Initialize PostHog with API key and configuration.
     * This method is called during app initialization via APP_INITIALIZER.
     * 
     * @returns Promise that resolves when PostHog is initialized or if disabled
     * @example
     * // Called automatically in app.module.ts
     * await posthogService.initialize();
     */
    initialize(): Promise<void> {
        return new Promise((resolve) => {
            if (!this.isEnabled()) {
                resolve();
                return;
            }

            if (this.initialized) {
                resolve();
                return;
            }

            try {
                // Set initialized to true immediately after calling init
                // PostHog will queue events until it's fully loaded
                this.initialized = true;
                this.ngZone.runOutsideAngular(() => {
                    posthog.init(environment.posthogApiKey, {
                        api_host: environment.posthogApiHost,
                        loaded: () => {
                            // PostHog is now loaded and ready
                            resolve();
                        }
                    });
                });
            } catch (error) {
                console.error('Failed to initialize PostHog:', error);
                this.initialized = false;
                resolve(); // Resolve even on error to not block app startup
            }
        });
    }

    /**
     * Identify a user in PostHog with their properties.
     * Associates all subsequent events with this user.
     * 
     * @param user - User object containing identification and property data
     * @example
     * posthogService.identify({
     *   id: user.id,
     *   userName: user.userName,
     *   email: user.email,
     *   accountId: user.accountId
     * });
     */
    identify(user: PostHogUser): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            const userId = this.validateAndGetUserId(user);
            if (!userId) {
                console.warn('User ID is required for PostHog identification');
                return;
            }

            const userProperties = this.extractUserProperties(user);
            const cleanUserProperties = this.sanitizeProperties(userProperties);
            posthog.identify(userId, cleanUserProperties);
        } catch (error) {
            console.error('Failed to identify user in PostHog:', error);
        }
    }

    /**
     * Track a custom event with optional properties.
     * 
     * @param eventName - Name of the event to track (e.g., 'Button Clicked')
     * @param properties - Optional properties to attach to the event
     * @example
     * posthogService.capture('Report Generated', {
     *   reportType: 'circulation',
     *   dateRange: '30days'
     * });
     */
    capture(eventName: string, properties?: Record<string, unknown>): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            posthog.capture(eventName, properties);
        } catch (error) {
            console.error('Failed to capture PostHog event:', error);
        }
    }

    /**
     * Remove undefined properties from an object
     */
    private sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
        const cleanProperties: Record<string, unknown> = {};
        Object.keys(properties).forEach(key => {
            if (properties[key] !== undefined) {
                cleanProperties[key] = properties[key];
            }
        });
        return cleanProperties;
    }

    /**
     * Update user properties without changing identification.
     * Useful for updating context like library/branch selection.
     * 
     * @param properties - Properties to update on the current user
     * @example
     * posthogService.setPersonProperties({
     *   libraryName: 'Central Library',
     *   branchName: 'Downtown Branch'
     * });
     */
    setPersonProperties(properties: Record<string, unknown>): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            const cleanProperties = this.sanitizeProperties(properties);
            if (Object.keys(cleanProperties).length > 0) {
                posthog.setPersonProperties(cleanProperties);
            }
        } catch (error) {
            console.error('Failed to update PostHog person properties:', error);
        }
    }

    /**
     * Clear user identification and reset the PostHog session.
     * Should be called on user logout.
     * 
     * @example
     * // In logout flow
     * posthogService.reset();
     */
    reset(): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            posthog.reset();
            // User session reset
        } catch (error) {
            console.error('Failed to reset PostHog session:', error);
        }
    }

    /**
     * Validate and extract user ID from user object
     */
    private validateAndGetUserId(user: PostHogUser): string | null {
        if (!user || typeof user !== 'object') {
            return null;
        }

        // Check for id first, then userId as fallback
        let userId: string | number | undefined = user.id;
        if (!userId && user.userId) {
            userId = user.userId;
        }

        // Validate the ID is not null, undefined, empty string, or NaN
        if (userId === null || userId === undefined || userId === '' || (typeof userId === 'number' && isNaN(userId))) {
            return null;
        }

        return String(userId);
    }

    /**
     * Extract and validate user properties
     */
    private extractUserProperties(user: PostHogUser): Record<string, unknown> {
        // Property mapping configuration
        const propertyMappings: Array<{
            propName: string;
            userKeys: string[];
            validator: (value: unknown) => string | number | undefined;
        }> = [
            // String properties
            { propName: 'userName', userKeys: ['userName', 'username'], validator: (v) => this.safeString(v) },
            { propName: 'email', userKeys: ['email'], validator: (v) => this.safeString(v) },
            { propName: 'language', userKeys: ['language'], validator: (v) => this.safeString(v) },
            { propName: 'timeZone', userKeys: ['timeZone', 'timezone'], validator: (v) => this.safeString(v) },
            { propName: 'libraryName', userKeys: ['libraryName'], validator: (v) => this.safeString(v) },
            { propName: 'branchName', userKeys: ['branchName'], validator: (v) => this.safeString(v) },
            // Number or string properties
            { propName: 'accountId', userKeys: ['accountId'], validator: (v) => this.safeNumberOrString(v) },
            { propName: 'roleid', userKeys: ['roleid'], validator: (v) => this.safeNumberOrString(v) },
            { propName: 'libraryId', userKeys: ['libraryId'], validator: (v) => this.safeNumberOrString(v) },
            { propName: 'branchId', userKeys: ['branchId'], validator: (v) => this.safeNumberOrString(v) }
        ];

        const properties: Record<string, unknown> = {};

        // Process each property mapping
        propertyMappings.forEach(({ propName, userKeys, validator }) => {
            // Try each possible key until we find a valid value
            for (const key of userKeys) {
                const value = validator(user[key]);
                if (value !== undefined) {
                    properties[propName] = value;
                    break;
                }
            }
        });

        return properties;
    }

    /**
     * Safely extract string properties
     */
    private safeString(value: unknown): string | undefined {
        if (value === null || value === undefined) return undefined;
        if (typeof value === 'string' && value.trim() !== '') return value.trim();
        return undefined;
    }

    /**
     * Safely extract number or string properties
     */
    private safeNumberOrString(value: unknown): string | number | undefined {
        if (value === null || value === undefined) return undefined;
        if (typeof value === 'string' && value.trim() !== '') return value.trim();
        if (typeof value === 'number' && !isNaN(value)) return value;
        return undefined;
    }

    /**
     * Check if PostHog is enabled in the current environment.
     * 
     * @returns true if PostHog is enabled, false otherwise
     * @example
     * if (posthogService.isEnabled()) {
     *   // Analytics is active
     * }
     */
    isEnabled(): boolean {
        return environment.enablePosthog === true;
    }

    /**
     * Get the underlying PostHog instance for advanced usage.
     * Use with caution - prefer service methods when possible.
     * 
     * @returns PostHog instance if enabled and initialized, null otherwise
     * @example
     * const ph = posthogService.getInstance();
     * if (ph) {
     *   // Advanced PostHog operations
     * }
     */
    getInstance(): typeof posthog | null {
        return this.isEnabled() && this.initialized ? posthog : null;
    }
    

    /**
     * Check if PostHog is initialized and ready to use.
     * 
     * @returns true if PostHog is initialized and enabled
     * @example
     * if (posthogService.isInitialized()) {
     *   posthogService.capture('Ready Event');
     * }
     */
    isInitialized(): boolean {
        return this.initialized && this.isEnabled();
    }

    /**
     * Track user login event and identify the user.
     * Combines identification and login event tracking.
     * 
     * @param user - User object from authentication
     * @param libraryName - Optional library name for context
     * @param branchName - Optional branch name for context
     * @example
     * posthogService.trackUserLogin(
     *   authUser,
     *   'Main Library',
     *   'Downtown Branch'
     * );
     */
    trackUserLogin(user: User, libraryName?: string, branchName?: string): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            // Identify user
            this.identify({
                id: user.id,
                userName: user.userName,
                email: user.email,
                accountId: user.accountId,
                roleid: user.roleid,
                timeZone: user.timeZone,
                language: user.language,
                libraryName: libraryName,
                branchName: branchName
            });
            
            // Track login event
            this.capture('User Login', {
                accountId: user.accountId,
                roleid: user.roleid,
                language: user.language
            });
        } catch (error) {
            console.error('Failed to track user login:', error);
        }
    }

    /**
     * Track user logout event.
     * 
     * @param userId - ID of the user logging out
     * @param accountId - Account ID of the user
     * @example
     * posthogService.trackUserLogout(user.id, user.accountId);
     */
    trackUserLogout(userId: string | number, accountId: string | number): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            this.capture('User Logout', {
                userId: userId,
                accountId: accountId
            });
        } catch (error) {
            console.error('Failed to track user logout:', error);
        }
    }

    /**
     * Track session restoration from local storage.
     * Re-identifies the user and tracks the restoration event.
     * 
     * @param user - User object from restored session
     * @param libraryName - Optional library name for context
     * @param branchName - Optional branch name for context
     * @example
     * posthogService.trackSessionRestored(
     *   restoredUser,
     *   libraryService.getCurrentLibraryName(),
     *   libraryService.getCurrentBranchName()
     * );
     */
    trackSessionRestored(user: User, libraryName?: string, branchName?: string): void {
        if (!this.isEnabled() || !this.initialized) {
            return;
        }

        try {
            // Re-identify user
            this.identify({
                id: user.id,
                userName: user.userName,
                email: user.email,
                accountId: user.accountId,
                roleid: user.roleid,
                timeZone: user.timeZone,
                language: user.language,
                libraryName: libraryName,
                branchName: branchName
            });
            
            // Track session restored event
            this.capture('Session Restored', {
                accountId: user.accountId,
                roleId: user.roleid
            });
        } catch (error) {
            console.error('Failed to track session restoration:', error);
        }
    }
}
